import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { Typography } from './typography';

import { Icon } from '@/shared/assets/icons';
import { TooltipElement } from './tooltip';

type InputProps = {
  label?: string;
  helpText?: string;
  error?: boolean;
  errorText?: string;
  debounceMs?: number;
  leftNode?: ReactNode;
  rightNode?: ReactNode;
  inputClassName?: string;
  inputWrapperClassName?: string;
  required?: boolean;
  isLabelColumn?: boolean;
  bottomGutter?: boolean;
  infoText?: string;
} & ComponentPropsWithoutRef<'input'>;

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      // debounceMs = 0,
      error = false,
      required = false,
      bottomGutter = true,
      isLabelColumn = true,
      label,
      style,
      disabled,
      helpText,
      errorText,
      leftNode,
      rightNode,
      className,
      inputClassName,
      inputWrapperClassName,
      infoText,
      ...inputProps
    },
    ref
  ) => {
    return (
      <div
        data-field-error={error ? 'true' : undefined}
        className={cn(
          'flex w-full',
          isLabelColumn ? 'flex-col' : 'items-center gap-2',
          className
        )}
      >
        {label && (
          <div
            className={cn(
              'flex items-center gap-1',
              isLabelColumn ? 'pb-2' : ''
            )}
          >
            {required && (
              <Icon name="required" className="text-dust-red-5" size={10} />
            )}
            <Typography>{label}</Typography>
            {!!infoText && <TooltipElement content={infoText} />}
          </div>
        )}

        <div
          style={style}
          className={cn(
            'border-neutral-5 bg-neutral-1 flex w-full items-center gap-1 rounded-xs border px-3 py-1',
            inputWrapperClassName,
            error
              ? 'border-dust-red-5'
              : 'border-neutral-5 focus-within:border-primary-6',
            disabled &&
              'bg-neutral-3 border-neutral-5 cursor-not-allowed border opacity-50'
          )}
        >
          {leftNode}
          <input
            ref={ref}
            disabled={disabled}
            {...inputProps}
            className={cn(
              'h-[30px] w-full text-[14px] leading-[22px] focus:ring-0 focus:outline-none',
              disabled && 'cursor-not-allowed',
              inputClassName
            )}
          />
          {rightNode}
        </div>
        {bottomGutter && (
          <div className={cn((helpText ?? error) && 'min-h-6 w-full')}>
            {errorText && error && (
              <Typography
                className="text-dust-red-5"
                variant="footnote-description"
              >
                {errorText}
              </Typography>
            )}
            {!error && helpText && (
              <Typography className="opacity-45" variant="footnote-description">
                {helpText}
              </Typography>
            )}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'UI/Input';
